import React from "react";
import { statusColors } from "@/utils/statusUtils.ts";
import { StatusId, stringEmDash, TIME_FORMAT } from "@/constants/app_constants.ts";
import {
  DELIVERED,
  DELIVERED_DATE,
  DELIVERY_DOCKET,
  DESCRIPTION,
  DRAWING,
  JOB_CODE,
  JOB_LOCATION,
  READY,
  SOMETHING_WENT_WRONG,
  VIEW_LESS,
  VIEW_MORE
} from "@/constants/strings.ts";
import { Images } from "@/constants/images.tsx";
import { THEME } from "@/constants";
import { Button } from "antd";
import { ProjectItemModel } from "@/stores/viewProjectItemDetailStore.ts";
import { formatTimeDayMonthYear } from "@/components/table-ant/table-common.ts";
import { useToast } from "@/components/common/toast/ToastProvider";
import { useMutation } from "@tanstack/react-query";
import { commonService } from "@/services/commonService";
import { ToastType } from "@/components/common/toast/Toast";
import { isIOS, isIOS13, isIPad13, isTablet } from "react-device-detect";
import BreakLine from "@/components/common/break_line";

interface ViewProjectItemDetailItemCardProps {
  item: ProjectItemModel;
  handlePrintDeliveryDocket: (item: string) => void;
}

export const ViewProjectItemDetailItemCard: React.FC<ViewProjectItemDetailItemCardProps> = ({ item, handlePrintDeliveryDocket }) => {
  const [isShowMore, setIsShowMore] = React.useState(false);
  const { showToast } = useToast();
  const userAgent = navigator.userAgent.toLowerCase();
  const isIPad = /iPad|Macintosh/i.test(userAgent) && /Safari/i.test(userAgent) && !/Chrome|Android/i.test(userAgent);

  // iOS only open view mode once trigger event directly inside button
  const usePrintDeliveryDocketIOS = useMutation<any, Error, string>({
    mutationFn: deliveryDocketNo =>
      commonService.printDeliveryDocketIOS({
        deliveryDocketNo: deliveryDocketNo
      })
  });

  return (
    <div className="border-1 rounded-md w-full ml-3 mr-3" style={{ borderColor: statusColors[item.status ?? StatusId.inProduction] }}>
      {/* Header */}
      <h3 className="font-semibold text-lg mb-2 text-gray-900 pt-3 pl-3 pr-3">{item.pieceNo}</h3>
      <div className="pl-3 pr-3">
        <BreakLine />
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-2 gap-y-3 text-sm mb-2 p-3 gap-x-3">
        <div className="w-full col-span-1">
          <div className="text-gray-700 text-xs">{READY}</div>
          <div className="font-semibold text-sm text-gray-900 mt-[6px]">{item.itemReadyQty}</div>
        </div>
        <div>
          <div className="text-gray-700 text-xs">{DELIVERED}</div>
          <div className="font-semibold text-sm text-gray-900 mt-[6px]">{item.itemDeliveredQty}</div>
        </div>
        <div>
          <div className="text-gray-700 text-xs">{DESCRIPTION}</div>
          <div className="font-semibold text-sm text-gray-900 mt-[6px]">{item.description ? item.description : stringEmDash}</div>
        </div>
        <div>
          <div className="text-gray-700 text-xs">{JOB_CODE}</div>
          <div className="font-semibold text-sm text-gray-900 mt-[6px]">{item.jobCodeAndPart}</div>
        </div>
        <div>
          <div className="text-gray-700">{DRAWING}</div>
          <div className="font-semibold text-sm text-gray-900 mt-[6px]">{item.drawingNumber ? item.drawingNumber : stringEmDash}</div>
        </div>
        <div>
          <div className="text-gray-700 text-xs">{JOB_LOCATION}</div>
          <div className="font-semibold text-sm text-gray-900 mt-[6px]">{item.jobLocation ? item.jobLocation : stringEmDash}</div>
        </div>
        {isShowMore && (
          <>
            <div>
              <div className="text-gray-700 text-xs">{DELIVERED_DATE}</div>
              <div className="font-semibold text-sm text-gray-900 mt-[6px]">
                {item.itemDeliveredDate ? formatTimeDayMonthYear(item.itemDeliveredDate, TIME_FORMAT.ISO) : stringEmDash}
              </div>
            </div>
            <div>
              <div className="text-gray-700 text-xs">{DELIVERY_DOCKET}</div>
              {item.deliveryNumbers && item.deliveryNumbers.length > 0 ? (
                <div className="space-y-2 mt-[6px]">
                  {item.deliveryNumbers.map((singleItem, index) => (
                    <Button
                      key={index}
                      icon={<img src={Images.IconDownload} alt="download" />}
                      className="text-orange-600 rounded-md border border-gray-300 cursor-pointer inline-flex justify-end items-start"
                      style={{
                        height: "32px",
                        color: THEME.COLORS.ORANGE_600
                      }}
                      onClick={() => {
                        // iOS only open view mode once trigger event directly inside button
                        if (isIOS || isIOS13 || isIPad13 || isTablet || isIPad) {
                          const printWindow = window.open("", "_blank");
                          if (!printWindow) {
                            showToast(ToastType.error, SOMETHING_WENT_WRONG);
                            return;
                          }
                          // Start fetching HTML content
                          usePrintDeliveryDocketIOS.mutate(singleItem, {
                            onSuccess: (data: any) => {
                              const parser = new DOMParser();
                              const doc = parser.parseFromString(data, "text/html");

                              // Format Delivery Date
                              const deliveryDateEl = doc.querySelector("#info .item .info-value");
                              if (deliveryDateEl?.textContent?.trim()) {
                                const deliveryDate = new Date(deliveryDateEl.textContent.trim());
                                const formatted = `${deliveryDate.getDate().toString().padStart(2, "0")}/${(deliveryDate.getMonth() + 1)
                                  .toString()
                                  .padStart(
                                    2,
                                    "0"
                                  )}/${deliveryDate.getFullYear()} ${deliveryDate.getHours().toString().padStart(2, "0")}:${deliveryDate.getMinutes().toString().padStart(2, "0")}`;
                                deliveryDateEl.textContent = formatted;
                              }

                              // Format all Scan Time
                              const scanTimeCells = doc.querySelectorAll("#table3 tbody tr td:first-child");
                              scanTimeCells.forEach(cell => {
                                if (cell.textContent?.trim()) {
                                  const scanTimeDate = new Date(cell.textContent.trim());
                                  const formatted = `${scanTimeDate.getDate().toString().padStart(2, "0")}/${(scanTimeDate.getMonth() + 1)
                                    .toString()
                                    .padStart(
                                      2,
                                      "0"
                                    )}/${scanTimeDate.getFullYear()} ${scanTimeDate.getHours().toString().padStart(2, "0")}:${scanTimeDate.getMinutes().toString().padStart(2, "0")}`;
                                  cell.textContent = formatted;
                                }
                              });

                              const updatedHtml = "<!DOCTYPE html>\n" + doc.documentElement.outerHTML;
                              printWindow.document.open();
                              printWindow.document.write(updatedHtml);
                              printWindow.document.close();
                            },
                            onError: () => {
                              printWindow.document.body.innerHTML = `<p>${SOMETHING_WENT_WRONG}</p>`;
                            }
                          });
                        } else {
                          handlePrintDeliveryDocket(singleItem);
                        }
                      }}
                    >
                      {singleItem}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="font-semibold text-sm text-gray-900 mt-[6px]">{stringEmDash}</div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="text-blue-600 text-sm mb-3 cursor-pointer" onClick={() => setIsShowMore(!isShowMore)}>
        {isShowMore ? (
          <div className="flex items-center gap-1 justify-center">
            {VIEW_LESS} <img src={Images.IconViewLessMobile} alt="View less" />
          </div>
        ) : (
          <div className="flex items-center gap-1 justify-center">
            {VIEW_MORE} <img src={Images.IconViewMoreMobile} alt="View more" />
          </div>
        )}
      </div>

      {/* Footer Buttons */}
      <div className="w-full">
        <div
          className="flex-1 text-white text-center py-2 rounded-br-md rounded-bl-md"
          style={{ backgroundColor: statusColors[item.status ?? StatusId.inProduction] }}
        >
          {item.status}
        </div>
      </div>
    </div>
  );
};
